import type { SvgIconComponent } from "@mui/icons-material";
import BeachAccessOutlinedIcon from "@mui/icons-material/BeachAccessOutlined";
import CelebrationOutlinedIcon from "@mui/icons-material/CelebrationOutlined";
import DynamicFeedOutlinedIcon from "@mui/icons-material/DynamicFeedOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import EventRepeatRoundedIcon from "@mui/icons-material/EventRepeatRounded";
import PanToolAltOutlinedIcon from "@mui/icons-material/PanToolAltOutlined";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import PhoneDisabledOutlinedIcon from "@mui/icons-material/PhoneDisabledOutlined";
import type { TFunction } from "i18next";
import {
	ACTION_NAME_VALUES,
	actorDisplay,
	BULK_UPDATE_ACTION_NAME,
	CHANNEL_LABEL_KEY,
	composeWhatSublabel,
	composeWhatText,
	formatActivityLogWhen,
	HOLIDAY_ACTION_NAME,
} from "../../pages/ActivityLog/GroupedActivityLog/activityLogGroupedDisplay";
import { type ResolvedGroupedActivityLogEntry, ShiftOrigin } from "../../pages/ActivityLog/GroupedActivityLog/useGroupedActivityLogQuery";

export const SHIFT_HISTORY_TEST_ID = "shift-history";

type ActionName = (typeof ACTION_NAME_VALUES)[number];

const ACTION_ICON: Record<ActionName, SvgIconComponent> = {
	"User Status": PersonOutlineRoundedIcon,
	"Call Off": PhoneDisabledOutlinedIcon,
	PTO: BeachAccessOutlinedIcon,
	Request: PanToolAltOutlinedIcon,
	Recurring: EventRepeatRoundedIcon,
	Shift: EventNoteOutlinedIcon,
	[HOLIDAY_ACTION_NAME]: CelebrationOutlinedIcon,
	Other: EditNoteOutlinedIcon,
	[BULK_UPDATE_ACTION_NAME]: DynamicFeedOutlinedIcon,
};

const isActionName = (value: string): value is ActionName => (ACTION_NAME_VALUES as readonly string[]).includes(value);

export const actionIcon = (actionName: string): SvgIconComponent => {
	if (!isActionName(actionName)) throw new Error(`Shift history has no icon for action "${actionName}"`);
	return ACTION_ICON[actionName];
};

/** The origin card's title for each way a shift gets on the schedule. */
export const ORIGIN_TITLE_KEY: Record<ShiftOrigin, string> = {
	[ShiftOrigin.Recurring]: "shiftHistory.origin.recurring.title",
	[ShiftOrigin.Split]: "shiftHistory.origin.split.title",
	[ShiftOrigin.Request]: "shiftHistory.origin.request.title",
	[ShiftOrigin.Swap]: "shiftHistory.origin.swap.title",
	[ShiftOrigin.Imported]: "shiftHistory.origin.imported.title",
	[ShiftOrigin.CopiedFromWorked]: "shiftHistory.origin.copied.title",
	[ShiftOrigin.Added]: "shiftHistory.origin.added.title",
};

const joined = (parts: (string | null)[]): string | null => parts.filter(Boolean).join(" · ") || null;

/** A resolved entry's words: what happened, the shift or pattern it was about, and when · who · channel. */
export type EntryCopy = { what: string; summary: string | null; meta: string };

export const describeEntry = (entry: ResolvedGroupedActivityLogEntry, timezone: string, t: TFunction): EntryCopy => {
	const actor = actorDisplay({ firstName: entry.performedByFirstName, username: entry.performedBy }).label;
	const channel = entry.channel === null ? null : t(CHANNEL_LABEL_KEY[entry.channel]);
	const reason = entry.details?.kind === "Shift" ? entry.details.reason : null;
	const meta = joined([formatActivityLogWhen(entry.changedAt, timezone), actor, channel]);
	if (meta === null) throw new Error(`Shift history entry ${entry.apiRequestId} has no time`);
	return { what: composeWhatText(entry.what, entry.details, t), summary: joined([composeWhatSublabel(entry.details, timezone, t), reason]), meta };
};
